import { ReactElement, Suspense } from 'react';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import { ColSizes, ROW_GUTTER } from 'utils/constants/app/ui';
import { SPORTSBOOK_WIDGETS_IDS } from 'utils/constants/sportsbook/sportsbook-widgets-ids';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { ESPORTS_ADDITIONAL_DATA } from 'utils/helpers/moba-games-statistics-options';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { EPageTypes } from 'interfaces/sportsbook';
import {
  ESportsbookPageTypes,
  TSportsbookConfigs
} from 'interfaces/sportsbook-configs';
import { DesktopGameListSkeleton } from 'elements/DesktopGameList/DesktopGameListSkeleton';
import { DesktopSingleGameViewSkeleton } from 'elements/DesktopSingleGameView/DesktopSingleGameViewSkeleton';
import { SingleGameView } from 'newelements/Sportsbook/__partials/SingleGameView';
import { SingleGameViewSkeleton } from 'newelements/Sportsbook/__partials/SingleGameView/SingleGameViewSkeleton';
import { INFINITE_SCROLL_ID } from 'newelements/Sportsbook/layouts/classic/DesktopSportsbookClassic';
import { GamesByCompetitionsCollapse } from 'newelements/Sportsbook/layouts/classic/GamesByCompetitionsCollapse';
import { SportsbookWidgetsHeightWrapper } from 'newelements/Sportsbook/Widgets/helpers/SportsbookWidgetsHeightWrapper';
import { DesktopMultipleCardsSkeleton } from 'components/MultipleSelections/MultipleSceleton/DesktopMultipleCardsSkeleton';
import { FavoriteTeamsBody } from 'newcomponents/Shared/sportsbook/FavoriteTeam/partials/FavoriteTeamsBody';
import { FavoriteTeamsHeader } from 'newcomponents/Shared/sportsbook/FavoriteTeam/partials/FavoriteTeamsHeader';
import { SportsbookGameListWrapper } from 'newcomponents/Shared/sportsbook/SportsbookGameListWrapper';
import { GameListWrapperSkeleton } from 'newcomponents/Shared/sportsbook/SportsbookGameListWrapper/skeleton';
import { Col } from 'newcomponents/UI/Col';
import { Row } from 'newcomponents/UI/Row';
import { getShowNoItemWrapper } from 'store/selectors/new-sportsbook';
import { getCoupons } from 'store/selectors/sport-data';

const { SportsbookMultiples } = lazy(
  () =>
    import(
      /* webpackChunkName: "multiple-cards-desktop" */ 'newelements/Sportsbook/__partials/SportsbookMultiples'
    )
);

const { CouponGameListWrapper } = lazy(
  () =>
    import(
      /* webpackChunkName: "couponGame-list-wrapper" */ 'newcomponents/Shared/sportsbook/CouponGameListWrapper'
    )
);

type TSecondColumnContentProps = {
  category?: ENotRealSports | string;
  isLive: boolean;
  configs: TSportsbookConfigs;
  paramsSport: string;
  isSeparatedWidget?: boolean;
};

export const SecondColumnContent = ({
  category,
  isLive,
  configs,
  paramsSport,
  isSeparatedWidget
}: TSecondColumnContentProps): ReactElement => {
  const showNoItemWrapper = useSelector(getShowNoItemWrapper);
  const coupons = useSelector(getCoupons);

  const isTopMatches = paramsSport === ENotRealSports.topMatches;

  const hasAdditionalStatistics =
    Object.keys(ESPORTS_ADDITIONAL_DATA).includes(paramsSport) && isLive;

  const isMultiples = paramsSport === ENotRealSports.multiples;

  if (isMultiples) {
    return (
      <Suspense
        fallback={
          <DesktopMultipleCardsSkeleton className="multiplesSkeleton" />
        }
      >
        <SportsbookMultiples configs={configs} />
      </Suspense>
    );
  }

  const isFavTeam = paramsSport === ENotRealSports.team;
  const isBoosted = paramsSport === ENotRealSports.boostedOdds;

  const isCoupon = paramsSport === ENotRealSports.coupon;

  return (
    <Row
      className={cc([
        'SBRow innerRow markets__container',
        {
          noPadding: SpringConfigs.LEFT_SIDE_BAR || isLive,
          'SBRow--cover': configs.fit === 'cover',
          separatedWidgetsRowNoMargin: isSeparatedWidget
        }
      ])}
      gutterV={ROW_GUTTER._8}
      gutterH={ROW_GUTTER._8}
    >
      {isFavTeam ? (
        <Col style={{ width: '100%' }} flex="1">
          <FavoriteTeamsHeader />
          <FavoriteTeamsBody />
        </Col>
      ) : (
        <>
          {!isLive && (
            <Col
              className={cc([{ SBCol: configs.fit === 'cover' }])}
              xs={ColSizes.SIZE_8}
              {...(configs.pageType === ESportsbookPageTypes.prematch && {
                id: INFINITE_SCROLL_ID
              })}
            >
              <SportsbookWidgetsHeightWrapper
                shouldWrap={!configs.fixedScroll && !!isSeparatedWidget}
                childrenElementId={SPORTSBOOK_WIDGETS_IDS.sportsbookGamesList}
              >
                {isBoosted || isTopMatches ? (
                  <GamesByCompetitionsCollapse
                    sportCategory={paramsSport as ENotRealSports}
                  />
                ) : (
                  <Suspense
                    fallback={
                      <DesktopGameListSkeleton
                        category={category}
                        isLive={isLive}
                      />
                    }
                  >
                    {isCoupon ? (
                      <Suspense
                        fallback={<GameListWrapperSkeleton isLive={false} />}
                      >
                        <CouponGameListWrapper />
                      </Suspense>
                    ) : (
                      <SportsbookGameListWrapper
                        pageType={EPageTypes.prematch}
                      />
                    )}
                  </Suspense>
                )}
              </SportsbookWidgetsHeightWrapper>
            </Col>
          )}
          <Col
            className={cc([{ SBCol: configs.fit === 'cover' }])}
            xs={isLive ? ColSizes.SIZE_24 : ColSizes.SIZE_16}
            style={{
              overflow: 'hidden',
              display: showNoItemWrapper?.game ? 'none' : 'flex'
            }}
          >
            <SportsbookWidgetsHeightWrapper
              shouldWrap={!configs.fixedScroll && !!isSeparatedWidget}
              childrenElementId={SPORTSBOOK_WIDGETS_IDS.sportsbookSingleView}
            >
              <Suspense
                fallback={<DesktopSingleGameViewSkeleton configs={configs} />}
              >
                {isCoupon && !coupons.games ? (
                  <SingleGameViewSkeleton
                    isLive={isLive}
                    hasAdditionalStatistics={hasAdditionalStatistics}
                  />
                ) : (
                  <SingleGameView />
                )}
              </Suspense>
            </SportsbookWidgetsHeightWrapper>
          </Col>
        </>
      )}
    </Row>
  );
};
